import authService from './authService';
import { OrderStatusConfig, OrderStatusFormData, OrderStatusSearchModel } from '../types/orderStatus';
import { PaginatedResponse, ApiResponse } from '../types/common';

class OrderStatusService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  async getAll(searchModel: OrderStatusSearchModel): Promise<PaginatedResponse<OrderStatusConfig>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/order-statuses?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar status de pedido');
    }

    const json = await response.json();
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  async getById(id: number): Promise<OrderStatusConfig> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/order-statuses/${id}`);

    if (!response.ok) {
      if (response.status === 404) throw new Error('Status de pedido não encontrado');
      throw new Error('Falha ao buscar status de pedido');
    }

    const data: ApiResponse<OrderStatusConfig> = await response.json();
    return data.data!;
  }

  async create(formData: OrderStatusFormData): Promise<OrderStatusConfig> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/order-statuses', formData);

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao criar status de pedido');
    }

    const data: ApiResponse<OrderStatusConfig> = await response.json();
    return data.data!;
  }

  async update(id: number, formData: OrderStatusFormData): Promise<OrderStatusConfig> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/order-statuses/${id}`, formData);

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao atualizar status de pedido');
    }

    const data: ApiResponse<OrderStatusConfig> = await response.json();
    return data.data!;
  }

  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/order-statuses/${id}`);

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao excluir status de pedido');
    }
  }
}

export const orderStatusService = new OrderStatusService();
export default orderStatusService;
